import { memo, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  FormLabel,
  Box,
  CircularProgress,
  Stack,
  Switch,
  Skeleton,
  Grid,
  Typography,
  Paper,
} from "@mui/material";
import NewItemFab from "../../../../components/new-item-fab";
import PreviewCard from "../../../../components/preview-card";
import { useNavigate, useParams } from "react-router-dom";
import { Assessment } from "../../../../models/assessment";
import AssessmentCard from "../components/assessment-preview-card";
import NewAssessmentDialog from "../components/new-assessment-dialog";
import LoadingSkeleton from "../../components/loading-skeleton";
import BackdropLoader from "../../../../components/backdrop-loader";
import { useClassroomActions } from "../../../../hooks/useClassroomActions";
import { AddCircleOutline } from "@mui/icons-material";
import { useSnackbar } from "../../../../components/snackbar";
import { useDomainActions } from "../../../../hooks/useDomainActions";

interface AssessmentsProps {
  hubActive: boolean | null;
}

function Assessments({ hubActive }: AssessmentsProps) {
  const { classroom_id } = useParams<{ classroom_id: string }>();
  const { 
    classrooms, 
    getAssessments,
    deleteAssessment
  } = useClassroomActions();
  
  const classroom = classrooms?.[Number(classroom_id)];
  const assessments = classroom?.assessments;
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [open, setOpen] = useState(false);

  const [runningTask, setRunningTask] = useState(-1);

  const { showMessage } = useSnackbar();

  const preCreate = () => {
    if (!hubActive) {
      showMessage('Hub is not activated', 1);
      return;
    }

    if (runningTask !== -1) {
      showMessage('please wait', 1);
      return;
    };

    setOpen(true);
  }

  // const _updateAssessment = async (ass: Assessment) => {
  //   return await updateAssessment(
  //     Number(classroom_id),
  //     ass
  //   );
  // };

  const _deleteAssessment = async (assessment_id: number) => {
    setDeleting(true);
    await deleteAssessment(Number(classroom_id), assessment_id);
    setDeleting(false);
  };

  useEffect(() => {
    const loadAssessments = async () => {
      await getAssessments(Number(classroom_id));
    };
    setLoading(true);
    if (classroom && !assessments) loadAssessments();
    setLoading(false);
  }, [classrooms]);

  if (loading || !assessments || hubActive === null) {
    return <LoadingSkeleton />;
  }

  const assessmentList = Object.values(assessments);


  return (
    <Box>
      {assessmentList.length > 0 ? (
        <Grid container spacing={2}>
          {assessmentList
            .sort((a, b) => new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime())
            .map((assessment) => (
              <Grid key={assessment.id} size={{ xs: 12, md: 6, lg: 4 }}>
                <AssessmentCard
                  assessment={assessment}
                  // updateAssessment={_updateAssessment}
                  onDelete={_deleteAssessment}
                  runningTask={runningTask}
                />
              </Grid>
            ))}
        </Grid>
      ) : (
         <Box>
            <Paper
              elevation={0}
              sx={{
                minHeight: 200,
                backgroundColor: "#f9fafb",
                border: "1px dashed #d1d5db",
                borderRadius: 2,
                px: 4,
                py: 6,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <AddCircleOutline sx={{ fontSize: 48, color: "#9ca3af", mb: 2 }} />

              <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                No Assessments Yet
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 300 }}>
                You haven’t created any assessments for this classroom yet.
              </Typography> 
            </Paper>
        </Box>
      )}
     
      <NewAssessmentDialog 
        open={open} 
        setOpen={setOpen} 
        classroom_id={classroom_id!}
        setRunningTask={setRunningTask}
      />
      
      <NewItemFab onClick={preCreate} />

      {deleting && <BackdropLoader/>}
    </Box>
  );
}

export default Assessments;